import { apiClient, unwrap } from './client';

/** Pointage des présences — P1 registre (docs/PLAN-POINTAGE.md). */

export interface HorairePointage {
  id:                    string;
  valableDu:             string;
  heureDebut:            string;
  heureFin:              string;
  pauseDebut:            string | null;
  pauseFin:              string | null;
  /** 1 = lundi … 7 = dimanche */
  joursTravailles:       number[];
  toleranceRetardMin:    number;
  /** null = selon décision PDG */
  retardRetenu:          boolean | null;
  hsAutorisationRequise: boolean;
  enVigueur:             boolean;
}

export interface NouvelHoraire {
  valableDu:             string;
  heureDebut:            string;
  heureFin:              string;
  pauseDebut?:           string;
  pauseFin?:             string;
  joursTravailles:       number[];
  toleranceRetardMin:    number;
  retardRetenu?:         boolean | null;
  hsAutorisationRequise: boolean;
}

export interface JourFerie { id: string; jour: string; libelle: string }

export interface EmployePointage {
  id:             string;
  matricule:      string;
  fullName:       string;
  department:     string;
  jobTitle:       string;
  numeroPointage: number | null;
}

export const pointageApi = {
  horaires:        async () => unwrap<HorairePointage[]>(await apiClient.get('/hr/pointage/horaires')),
  creerHoraire:    async (h: NouvelHoraire) => unwrap<HorairePointage[]>(await apiClient.post('/hr/pointage/horaires', h)),
  joursFeries:     async (annee: number) => unwrap<JourFerie[]>(await apiClient.get('/hr/pointage/jours-feries', { params: { annee } })),
  creerJourFerie:  async (j: { jour: string; libelle: string }) => unwrap<JourFerie>(await apiClient.post('/hr/pointage/jours-feries', j)),
  retirerJourFerie: async (id: string) => unwrap<{ id: string }>(await apiClient.delete(`/hr/pointage/jours-feries/${id}`)),
  employes:        async () => unwrap<EmployePointage[]>(await apiClient.get('/hr/pointage/employes')),
  attribuerNumero: async (id: string, numeroPointage: number | null) =>
    unwrap<EmployePointage>(await apiClient.patch(`/hr/pointage/employes/${id}/numero`, { numeroPointage })),
};

// ── P2 : bornes de pointage ──────────────────────────────────────────────────────────────
export interface BornePointage {
  id:              string;
  libelle:         string;
  site:            { code: string; libelle: string } | null;
  actif:           boolean;
  appareilAttache: boolean;
  createdAt:       string;
  dernierUsage:    string | null;
  revoqueLe:       string | null;
  motifRevocation: string | null;
  pointages:       number;
}

export const bornesApi = {
  lister:   async () => unwrap<BornePointage[]>(await apiClient.get('/hr/pointage/bornes')),
  /** Le lien (avec son secret) n'est rendu qu'une fois. */
  creer:    async (libelle: string) => unwrap<{ id: string; libelle: string; url: string }>(await apiClient.post('/hr/pointage/bornes', { libelle })),
  revoquer: async (id: string, motif: string) => unwrap<{ id: string }>(await apiClient.post(`/hr/pointage/bornes/${id}/revoquer`, { motif })),
};

// ── P3 : suivi RH ─────────────────────────────────────────────────────────────────────────
export type EtatPresence = 'present' | 'absent' | 'absence_justifiee' | 'ferie' | 'non_travaille' | 'attendu' | 'sans_horaire';
export type MotifAbsence = 'conge' | 'maladie' | 'permission' | 'mission' | 'autre';

export interface PassageAffiche {
  id:         string;
  horodatage: string;
  sens:       'entree' | 'sortie' | 'inconnu';
  source:     'borne' | 'pointeuse' | 'import' | 'correction';
  differe:    boolean;
  motif:      string | null;
  photo:      'disponible' | 'supprimee' | 'aucune';
}

export interface PresenceLigne {
  jour:               string;
  etat:               EtatPresence;
  arrivee:            string | null;
  depart:             string | null;
  retardMin:          number;
  departAvantMin:     number;
  incomplet:          boolean;
  horsJourTravaille:  boolean;
  minutesTravaillees: number | null;
  motifAbsence:       MotifAbsence | null;
  passages:           PassageAffiche[];
}

export interface PresenceDuJour {
  jour:      string;
  horaire:   boolean;
  ferie:     boolean;
  compteurs: { employes: number; presents: number; retards: number; departsAvant: number; incomplets: number; absents: number; justifies: number };
  lignes:    (PresenceLigne & { employe: EmployePointage })[];
}

export interface AbsenceJustifiee {
  id:          string;
  employeeId:  string;
  du:          string;
  au:          string;
  motif:       MotifAbsence;
  commentaire: string | null;
  piecePath:   string | null;
  annuleLe:    string | null;
  motifAnnulation: string | null;
  createdAt:   string;
  employee:    { id: string; fullName: string; matricule: string };
}

export const presenceApi = {
  jour:  async (jour: string) => unwrap<PresenceDuJour>(await apiClient.get('/hr/pointage/presence', { params: { jour } })),
  mois:  async (employeeId: string, mois: string) =>
    unwrap<{ employe: EmployePointage; mois: string; jours: PresenceLigne[] }>(await apiClient.get(`/hr/pointage/presence/employe/${employeeId}`, { params: { mois } })),
  photo: async (pointageId: string) => unwrap<{ url: string }>(await apiClient.get(`/hr/pointage/pointages/${pointageId}/photo`)),
  corriger: async (b: { employeeId: string; horodatage: string; sens: 'entree' | 'sortie'; motif: string }) =>
    unwrap<{ id: string }>(await apiClient.post('/hr/pointage/pointages', b)),
  annulerPointage: async (id: string, motif: string) => unwrap<{ id: string }>(await apiClient.post(`/hr/pointage/pointages/${id}/annuler`, { motif })),
  absences: async (q: { du?: string; au?: string; employeeId?: string; avecAnnulees?: boolean } = {}) =>
    unwrap<AbsenceJustifiee[]>(await apiClient.get('/hr/pointage/absences', { params: q })),
  deposerPiece: async (b: { dataBase64: string; contentType: string; nomOrigine?: string }) =>
    unwrap<{ path: string; url: string }>(await apiClient.post('/hr/pointage/absences/piece', b)),
  creerAbsence: async (b: { employeeId: string; du: string; au: string; motif: MotifAbsence; commentaire?: string; piecePath?: string }) =>
    unwrap<AbsenceJustifiee>(await apiClient.post('/hr/pointage/absences', b)),
  annulerAbsence: async (id: string, motif: string) => unwrap<{ id: string }>(await apiClient.post(`/hr/pointage/absences/${id}/annuler`, { motif })),
  pieceAbsence: async (id: string) => unwrap<{ url: string }>(await apiClient.get(`/hr/pointage/absences/${id}/piece`)),
};

// ── P4 : état mensuel ─────────────────────────────────────────────────────────────────────
export interface ReglesPaie {
  id?:                  string;
  valableDu?:           string;
  diviseurAbsence:      number;
  majorationHsPct:      number | null;
  majorationHsReposPct: number | null;
  enVigueur?:           boolean;
}

export interface EtatMensuelLigne {
  id:                    string;
  employeeId:            string;
  employee:              { id: string; matricule: string; fullName: string; department: string };
  salaireCents:          string | null;
  joursPrevus:           number;
  joursPresents:         number;
  absencesNonJustifiees: number;
  absencesJustifiees:    number;
  justifieesParMotif:    Record<string, number>;
  nbRetards:             number;
  retardTotalMin:        number;
  nbDepartsAvant:        number;
  departsAvantTotalMin:  number;
  incomplets:            number;
  joursReposTravailles:  number;
  hsFaitesMin:           number;
  hsAutoriseesMin:       number;
  hsCompteesMin:         number;
  hsReposCompteesMin:    number;
  retenueAbsenceCents:   string | null;
  montantHsCents:        string | null;
  /** Rattrapage (20/10/2026) : jours de suspension du contrat — salaire proratisé, ni absence ni présence. */
  joursSuspension:       number;
}

export interface EtatMensuel {
  mois:  string;
  etat:  null | { id: string; statut: 'brouillon' | 'valide'; calculeLe: string; valideLe: string | null; validePar: string | null;
                  rouvertLe: string | null; rouvertPar: string | null; motifReouverture: string | null;
                  origine: 'pointage' | 'rattrapage'; source: string | null };
  regles: ReglesPaie | null;
  montantsVisibles: boolean;
  lignes: EtatMensuelLigne[];
}

export interface AutorisationHs {
  id: string; employeeId: string; jour: string; minutes: number; motif: string;
  employee: { id: string; fullName: string };
}

export const etatApi = {
  lire:     async (mois: string) => unwrap<EtatMensuel>(await apiClient.get('/hr/pointage/etat-mensuel', { params: { mois } })),
  calculer: async (mois: string) => unwrap<EtatMensuel>(await apiClient.post('/hr/pointage/etat-mensuel/calculer', { mois })),
  valider:  async (mois: string) => unwrap<EtatMensuel>(await apiClient.post('/hr/pointage/etat-mensuel/valider', { mois })),
  rouvrir:  async (mois: string, motif: string) => unwrap<EtatMensuel>(await apiClient.post('/hr/pointage/etat-mensuel/rouvrir', { mois, motif })),
  corrigerRattrapage: async (mois: string, employeeId: string, l: { joursPrevus: number; joursPresents: number; suspendu: boolean }) =>
    unwrap<EtatMensuel>(await apiClient.post(`/hr/pointage/etat-mensuel/rattrapage/${employeeId}`, { mois, ...l })),
  regles:   async () => unwrap<ReglesPaie[]>(await apiClient.get('/hr/pointage/regles-paie')),
  creerRegles: async (r: { valableDu: string; diviseurAbsence: number; majorationHsPct: number | null; majorationHsReposPct: number | null }) =>
    unwrap<ReglesPaie[]>(await apiClient.post('/hr/pointage/regles-paie', r)),
  heuresSup: async (q: { jour?: string; mois?: string }) => unwrap<AutorisationHs[]>(await apiClient.get('/hr/pointage/heures-sup', { params: q })),
  autoriserHs: async (b: { employeeId: string; jour: string; minutes: number; motif: string }) =>
    unwrap<AutorisationHs>(await apiClient.post('/hr/pointage/heures-sup', b)),
  annulerHs: async (id: string, motif: string) => unwrap<{ id: string }>(await apiClient.post(`/hr/pointage/heures-sup/${id}/annuler`, { motif })),
};
